import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePublicBranding } from "@/hooks/usePublicBranding";
import { Truck, ArrowRight } from "lucide-react";

export default function Register() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const branding = usePublicBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    const ok = await register(email, password, name);
    setLoading(false);
    if (ok) nav("/");
  }

  const brandName = branding.app_name || "Maan group";

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
        <div className="flex items-center gap-2 mb-8">
          {branding.logo_data_url ? (
            <img src={branding.logo_data_url} alt={brandName} className="w-9 h-9 object-contain rounded-md" />
          ) : (
            <Truck className="w-6 h-6 text-[#2563EB]" />
          )}
          <span className="font-heading text-xl font-semibold">{brandName}</span>
        </div>
        <h2 className="font-heading text-3xl font-semibold">Create account</h2>
        <p className="text-sm text-slate-500 mt-2">Start tracking routes in seconds.</p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Name</label>
            <input
              data-testid="register-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Email</label>
            <input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Password</label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        {error && <p data-testid="register-error" className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          data-testid="register-submit"
          disabled={loading}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-60"
        >
          {loading ? "Creating..." : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
        </button>
        <p className="mt-6 text-sm text-slate-500 text-center">
          Have an account? <Link to="/login" data-testid="link-login" className="text-[#2563EB] hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
